import React from "react";
import { cn, Hyperlink } from "@racoon/ui";
import { useDateFnsLocale } from "@racoon/i18n";
import { format } from "date-fns";

import { formatUsage, NO_DATA } from "../helpers";

export const UsageCell: React.FC<
  Parameters<typeof formatUsage>[2] & { value?: number; unit: string }
> = ({ value, unit, ...options }) => (
  <span className="text-sm whitespace-nowrap">{formatUsage(value, unit, options)}</span>
);

export type DateFnsLocale = ReturnType<typeof useDateFnsLocale>;

/**
 * Time and date as two separate runs, the way the Figma row renders them
 * ("09:34   21 Дек 2026").
 */
export const DateTimeCell: React.FC<{
  value?: string;
  locale: DateFnsLocale;
  /** Planned times are muted, so a row reads fact-first. */
  muted?: boolean;
}> = ({ value, locale, muted }) => {
  const date = value ? new Date(value) : null;
  const valid = date && !Number.isNaN(date.getTime());

  return (
    <span
      className={cn(
        "flex flex-col leading-tight whitespace-nowrap",
        muted && "text-text-secondary",
      )}
    >
      <span>{valid ? format(date!, "HH:mm") : NO_DATA}</span>
      <span>{valid ? format(date!, "d MMM yyyy", { locale }) : NO_DATA}</span>
    </span>
  );
};

/**
 * `CreateShiftModal` opens by cloning its trigger and injecting `onClick`, which
 * `Hyperlink` does not take — it reports clicks through `onOpen`. This bridges
 * the two so the driver cell can use the platform hyperlink.
 */
export const TriggerHyperlink: React.FC<{
  /** Injected by the cloning parent; it takes an event but never reads one. */
  onClick?: () => void;
  title?: string;
  children: React.ReactNode;
}> = ({ onClick, title, children }) => (
  <Hyperlink title={title} onOpen={() => onClick?.()}>
    {children}
  </Hyperlink>
);
